import { Component, OnInit, Input, Output, ElementRef, EventEmitter } from '@angular/core';

import { CurrencyPipe } from '@angular/common';

import { DocumentSidebarComponent } from '../../documents/document-sidebar/document-sidebar.component';

import { Doc } from '../../../models/doc';
import { DocumentContact } from '../../../models/document-contact';
import { OutgoingDocument } from '../../../models/outgoing-document';
import { OutgoingJob } from '../../../models/outgoing-job';
import { OutgoingTransaction } from '../../../models/outgoing-transaction';

import { AlertService } from '../../../services/common/alert.service';
import { DocumentService } from '../../../services/documents/document.service';
import { DocumentStoreService } from '../../../services/documents/document-store.service';
import { MatterStoreService } from '../../../services/matters/matter-store.service';
import { OutgoingJobService } from '../../../services/outgoing/outgoing-job.service';

declare var jQuery: any;

@Component({
  selector: 'app-outgoing-form-dialog',
  templateUrl: './outgoing-form-dialog.component.html',
  styleUrls: ['./outgoing-form-dialog.component.css'],
})
export class OutgoingFormDialogComponent implements OnInit {
  @Input() matter_id: number;
  @Output() result: EventEmitter<string> = new EventEmitter();

  attachments: Array<any> = [];

  creatingOutgoingJob: boolean = false;

  // TODO: Handle when doc is not present in sidebar component
  doc: Doc | null = new Doc();

  documentToContacts: Array<DocumentContact>;
  documentCcContacts: Array<DocumentContact>;
  documentBccContacts: Array<DocumentContact>;

  involvements: any = {};

  matterSummaryContent: string = '';

  outgoingJob: OutgoingJob = new OutgoingJob();
  outgoingTransaction: OutgoingTransaction = new OutgoingTransaction();

  outgoingDocuments: Array<OutgoingDocument> = [];

  outgoingJobSummaryAllowed: boolean = false;

  proofTemplates: Array<{ id: number; name: string; default: boolean }> = [];
  selectedProofTemplateId: number | null = null;

  constructor(private alertService: AlertService, private currencyPipe: CurrencyPipe, private documentService: DocumentService, private documentStoreService: DocumentStoreService, private matterStoreService: MatterStoreService, private outgoingJobService: OutgoingJobService) {}

  // Component lifecycle events
  ngOnInit() {
    this.outgoingJob.status = 'pending';
    let documentSidebarComponent: DocumentSidebarComponent | null = this.documentStoreService.documentSidebarComponent;
    if (documentSidebarComponent) {
      this.doc = documentSidebarComponent.doc;
      this.documentToContacts = documentSidebarComponent.documentToContacts;
      this.documentCcContacts = documentSidebarComponent.documentCcContacts;
      this.documentBccContacts = documentSidebarComponent.documentBccContacts;
      this.involvements = documentSidebarComponent.involvements;
      for (let documentContact of this.documentToContacts) documentContact.attachments = {};
      for (let documentContact of this.documentCcContacts) documentContact.attachments = {};
      for (let documentContact of this.documentBccContacts) documentContact.attachments = {};
    }
    this.documentService.proofTemplates(this.matter_id).then((templates) => {
      this.proofTemplates = templates || [];
      const defaultTemplate = this.proofTemplates.find((t) => t.default) || this.proofTemplates[0];
      this.selectedProofTemplateId = defaultTemplate ? defaultTemplate.id : null;
    });
  }

  // When the top-level "Include Proof" toggles on, default every recipient's
  // per-row include_proof to true so users don't fall in the trap of clicking
  // Include Proof, previewing it, and ending up with no proof generated
  // because the (separate) per-recipient checkbox was left off.
  toggleIncludeProof() {
    this.outgoingTransaction.include_proof = !this.outgoingTransaction.include_proof;
    if (this.outgoingTransaction.include_proof) {
      for (let dc of this.documentToContacts || []) if (dc.include_proof === undefined || dc.include_proof === null) dc.include_proof = true;
      for (let dc of this.documentCcContacts || []) if (dc.include_proof === undefined || dc.include_proof === null) dc.include_proof = true;
    }
  }

  // Component methods
  attachmentsChanged(attachments: Array<any>) {
    this.attachments = attachments;
    for (let documentContact of this.documentToContacts) for (let attachment of this.attachments) documentContact.attachments[attachment.id] = true;
    for (let documentContact of this.documentCcContacts)
      for (let attachment of this.attachments) if (!String(documentContact.attachments[attachment.id]) || String(documentContact.attachments[attachment.id]) == 'undefined') documentContact.attachments[attachment.id] = documentContact.send_attachments;
    for (let documentContact of this.documentBccContacts)
      for (let attachment of this.attachments) if (!String(documentContact.attachments[attachment.id]) || String(documentContact.attachments[attachment.id]) == 'undefined') documentContact.attachments[attachment.id] = documentContact.send_attachments;
  }

  buildMatterSummaryContent() {
    this.matterSummaryContent = `
			<h3>${this.matterStoreService.matterShowComponent!.matter_info.number} - ${this.matterStoreService.matterShowComponent!.matter_info.case_name}</h3>
			<h3>Main Document:&nbsp;${this.doc!.name}</h3>
			<h5>Instructions:</h5>
			<ul style='list-style-type: disc;list-style-position: inside'>`;
    if (this.outgoingTransaction.special_instructions)
      this.matterSummaryContent += `<li>
					${this.outgoingTransaction.special_instructions}
				</li>`;
    if (this.outgoingTransaction.wait_for_attachments)
      this.matterSummaryContent += `<li>
					This job is stalled until attachments are delivered.
					<ul style='list-style-type:circle;list-style-position: inside;'>
						<li>
							<h5>Instructions&nbsp;</h5>
							<span>${this.outgoingTransaction.describe}</span>
						</li>
					</ul>
				</li>`;
    if (this.outgoingTransaction.needs_check)
      this.matterSummaryContent += `<li>
					A check will need to be cut for contact ${this.outgoingTransaction.check_payee_name} for the amount of ${this.currencyPipe.transform(this.outgoingTransaction.check_amount, 'USD', true, '1.2-2')}
				</li>`;
    if (this.outgoingTransaction.include_file_copy)
      this.matterSummaryContent += `<li>
					A file copy of the original document and the proof will be included.
				</li>`;
    if (this.outgoingTransaction.include_proof)
      this.matterSummaryContent += `<li>
					A proof will be created.
				</li>`;
    this.matterSummaryContent += `</ul>
			<h5>Recipient Instructions</h5>`;
    for (let documentToContact of this.documentToContacts) {
      this.matterSummaryContent += `<div>
				<h5 class='font-weight-normal'>To: ${documentToContact.copy_of_contact} Instructions:</h5>`;
      if (documentToContact.include_proof) {
        this.matterSummaryContent += `<div>
						<p>(Proof):</p>
						<ul style='list-style-type:disc;list-style-position: inside;'>`;
        if (documentToContact.send_by_mail) this.matterSummaryContent += `<li>${this.formatMailTo(documentToContact)}</li>`;
        if (documentToContact.send_by_email) this.matterSummaryContent += `<li>${this.formatEmailTo(documentToContact)}</li>`;
        if (documentToContact.send_by_fax) this.matterSummaryContent += `<li>${this.formatFaxTo(documentToContact)}</li>`;
        this.matterSummaryContent += `</ul>
					</div>`;
      }
      this.matterSummaryContent += `<div>
					<p>${this.doc!.name} (Main Document):</p>
					<ul style='list-style-type:disc;list-style-position: inside;'>`;
      if (documentToContact.send_by_mail) this.matterSummaryContent += `<li>${this.formatMailTo(documentToContact)}</li>`;
      if (documentToContact.send_by_email) this.matterSummaryContent += `<li>${this.formatEmailTo(documentToContact)}</li>`;
      if (documentToContact.send_by_fax) this.matterSummaryContent += `<li>${this.formatFaxTo(documentToContact)}</li>`;
      this.matterSummaryContent += `</ul>
				</div>`;
      for (let attachment of this.attachments) {
        this.matterSummaryContent += `<div>
					<p>${attachment.name} (Attachment):</p>
					<ul style='list-style-type:disc;list-style-position: inside;'>`;
        if (documentToContact.send_by_mail && !attachment.send_original) this.matterSummaryContent += `<li>${this.formatMailTo(documentToContact, attachment)}</li>`;
        if (documentToContact.send_by_email) this.matterSummaryContent += `<li>${this.formatEmailTo(documentToContact, attachment)}</li>`;
        if (documentToContact.send_by_fax) this.matterSummaryContent += `<li>${this.formatFaxTo(documentToContact, attachment)}</li>`;
        if (attachment.instructions) this.matterSummaryContent += `<li>Special Instructions: ${attachment.instructions}</li>`;
        if (attachment.send_original) this.matterSummaryContent += `<li><strong>Original document is to be sent.</strong></li>`;
        this.matterSummaryContent += `</ul>	
					</div>`;
      }
      this.matterSummaryContent += `</div>`;
    }
    for (let documentCcContact of this.documentCcContacts) {
      this.matterSummaryContent += `<div>
				<h5 class='font-weight-normal'>CC: ${documentCcContact.copy_of_contact} Instructions:</h5>`;
      this.matterSummaryContent += `<div>
					<p>${this.doc!.name} (Main Document):</p>
					<ul style='list-style-type:disc;list-style-position: inside;'>`;
      if (documentCcContact.send_by_mail) this.matterSummaryContent += `<li>${this.formatMailTo(documentCcContact)}</li>`;
      if (documentCcContact.send_by_email) this.matterSummaryContent += `<li>${this.formatEmailTo(documentCcContact)}</li>`;
      if (documentCcContact.send_by_fax) this.matterSummaryContent += `<li>${this.formatFaxTo(documentCcContact)}</li>`;
      this.matterSummaryContent += `</ul>
				</div>`;
      for (let attachment of this.attachments) {
        this.matterSummaryContent += `<div>
					<p>${attachment.name} (Attachment):</p>
					<ul style='list-style-type:disc;list-style-position: inside;'>`;
        if (documentCcContact.send_by_mail && !attachment.send_original) this.matterSummaryContent += `<li>${this.formatMailTo(documentCcContact, attachment)}</li>`;
        if (documentCcContact.send_by_email) this.matterSummaryContent += `<li>${this.formatEmailTo(documentCcContact, attachment)}</li>`;
        if (documentCcContact.send_by_fax) this.matterSummaryContent += `<li>${this.formatFaxTo(documentCcContact, attachment)}</li>`;
        if (attachment.instructions) this.matterSummaryContent += `<li>Special Instructions: ${attachment.instructions}</li>`;
        this.matterSummaryContent += `</ul>	
					</div>`;
      }
      this.matterSummaryContent += `</div>`;
    }
    for (let documentBccContact of this.documentBccContacts) {
      this.matterSummaryContent += `<div>
				<h5 class='font-weight-normal'>BCC: ${documentBccContact.copy_of_contact} Instructions:</h5>`;
      this.matterSummaryContent += `<div>
					<p>${this.doc!.name} (Main Document):</p>
					<ul style='list-style-type:disc;list-style-position: inside;'>`;
      if (documentBccContact.send_by_mail) this.matterSummaryContent += `<li>${this.formatMailTo(documentBccContact)}</li>`;
      if (documentBccContact.send_by_email) this.matterSummaryContent += `<li>${this.formatEmailTo(documentBccContact)}</li>`;
      if (documentBccContact.send_by_fax) this.matterSummaryContent += `<li>${this.formatFaxTo(documentBccContact)}</li>`;
      this.matterSummaryContent += `</ul>
				</div>`;
      for (let attachment of this.attachments) {
        this.matterSummaryContent += `<div>
					<p>${attachment.name} (Attachment):</p>
					<ul style='list-style-type:disc;list-style-position: inside;'>`;
        if (documentBccContact.send_by_mail && !attachment.send_original) this.matterSummaryContent += `<li>${this.formatMailTo(documentBccContact, attachment)}</li>`;
        if (documentBccContact.send_by_email) this.matterSummaryContent += `<li>${this.formatEmailTo(documentBccContact, attachment)}</li>`;
        if (documentBccContact.send_by_fax) this.matterSummaryContent += `<li>${this.formatFaxTo(documentBccContact, attachment)}</li>`;
        if (attachment.instructions) this.matterSummaryContent += `<li>Special Instructions: ${attachment.instructions}</li>`;
        this.matterSummaryContent += `</ul>	
					</div>`;
      }
      this.matterSummaryContent += `</div>`;
    }
  }

  cancel() {
    this.result.emit('cancelled');
  }

  clearPayee() {
    this.outgoingTransaction.check_payee_id = null;
    this.outgoingTransaction.check_payee_name = null;
  }

  createOutgoingJob(matterSummaryContent: Element) {
    this.alertService.alert_status_info_overlay('Outgoing jobs is being created <i class="fas fa-spinner fa-fw fa-pulse"></i>');
    this.creatingOutgoingJob = true;
    this.outgoingJob.instructions = (matterSummaryContent || document.getElementById('matter-summary-content')).innerHTML;
    let outgoingDocuments: Array<OutgoingDocument> = [];
    let documentContactSets: Array<any> = [
      {
        documentContacts: this.documentToContacts,
        type: 'DocumentToContact',
      },
      {
        documentContacts: this.documentCcContacts,
        type: 'DocumentCcContact',
      },
      {
        documentContacts: this.documentBccContacts,
        type: 'DocumentBccContact',
      },
    ];
    for (let documentContactSet of documentContactSets)
      for (let documentContact of documentContactSet.documentContacts) {
        let involvement: any = this.involvements[documentContact.involvement_id];
        if (this.outgoingTransaction.include_proof && documentContact.include_proof) {
          if (documentContact.send_by_mail)
            outgoingDocuments.push(<OutgoingDocument>{
              pages: 'all',
              copies: 1,
              involvement_id: involvement.id,
              way: 'print',
              destination: documentContactSet.type == 'DocumentToContact' ? 'quality' : 'copy',
              document_contact_id: documentContact.id,
            });
          if (documentContact.send_by_email)
            outgoingDocuments.push(<OutgoingDocument>{
              pages: 'all',
              copies: 1,
              involvement_id: involvement.id,
              way: 'share',
              destination: documentContact.copy_of_email_address,
              share_contact_id: involvement.contact.id,
              document_contact_id: documentContact.id,
            });
          if (documentContact.send_by_fax)
            outgoingDocuments.push(<OutgoingDocument>{
              pages: 'all',
              copies: 1,
              involvement_id: involvement.id,
              way: 'fax',
              destination: documentContact.copy_of_fax,
              document_contact_id: documentContact.id,
            });
        }
        if (documentContact.send_by_mail)
          outgoingDocuments.push(<OutgoingDocument>{
            pages: 'all',
            copies: documentContactSet.type != 'DocumentToContact' ? 1 : this.outgoingTransaction.copies,
            involvement_id: involvement.id,
            way: 'print',
            destination: documentContactSet.type == 'DocumentToContact' ? 'quality' : 'copy',
            document_id: this.doc!.id,
            document_contact_id: documentContact.id,
          });
        if (documentContact.send_by_email)
          outgoingDocuments.push(<OutgoingDocument>{
            pages: 'all',
            copies: this.outgoingTransaction.copies,
            involvement_id: involvement.id,
            way: 'share',
            destination: documentContact.copy_of_email_address,
            share_contact_id: involvement.contact.id,
            document_id: this.doc!.id,
            document_contact_id: documentContact.id,
          });
        if (documentContact.send_by_fax)
          outgoingDocuments.push(<OutgoingDocument>{
            pages: 'all',
            copies: this.outgoingTransaction.copies,
            involvement_id: involvement.id,
            way: 'fax',
            destination: documentContact.copy_of_fax,
            document_id: this.doc!.id,
            document_contact_id: documentContact.id,
          });
        if (this.doc!.attachments_included)
          for (let attachment of this.attachments) {
            if (!documentContact.attachments[attachment.id]) continue;
            if (documentContact.send_by_mail && (!attachment.send_original || documentContactSet.type != 'DocumentToContact'))
              outgoingDocuments.push(<OutgoingDocument>{
                pages: attachment.pages,
                copies: documentContactSet.type != 'DocumentToContact' ? 1 : attachment.copies,
                involvement_id: involvement.id,
                way: 'print',
                destination: attachment.quality && documentContactSet.type == 'DocumentToContact' ? 'quality' : 'attachment',
                document_id: attachment.id,
                document_contact_id: documentContact.id,
              });
            if (documentContact.send_by_email)
              outgoingDocuments.push(<OutgoingDocument>{
                pages: attachment.pages,
                copies: attachment.copies,
                involvement_id: involvement.id,
                way: 'share',
                destination: documentContact.copy_of_email_address,
                share_contact_id: involvement.contact.id,
                document_id: attachment.id,
                document_contact_id: documentContact.id,
              });
            if (documentContact.send_by_fax)
              outgoingDocuments.push(<OutgoingDocument>{
                pages: attachment.pages,
                copies: attachment.copies,
                involvement_id: involvement.id,
                way: 'fax',
                destination: documentContact.copy_of_fax,
                document_id: attachment.id,
                document_contact_id: documentContact.id,
              });
          }
      }
    let recipients: Array<any> = [];
    for (let documentContact of this.documentToContacts) recipients.push((this.formatRecipientForProof(documentContact) || '').replace(/'/g, "\\'"));
    for (let documentContact of this.documentCcContacts) recipients.push((this.formatRecipientForProof(documentContact) || '').replace(/'/g, "\\'"));
    this.outgoingJobService
      .create({
        document_id: this.doc!.id,
        include_file_copy: this.outgoingTransaction.include_file_copy,
        check_payee_id: this.outgoingTransaction.check_payee_id,
        check_amount: this.outgoingTransaction.check_amount,
        outgoing_job: this.outgoingJob,
        outgoing_documents: outgoingDocuments,
        matter_id: this.matter_id,
        proof_date: this.formatProofDate(this.outgoingTransaction.proof_date),
        type_served: this.outgoingTransaction.type_served,
        initial_run: true,
        doc_template_id: this.selectedProofTemplateId,
        recipients: recipients,
      })
      .then((response_json) => {
        this.creatingOutgoingJob = false;
        this.alertService.alert_status_clear_overlay();
        if (response_json && response_json.success === false) {
          this.alertService.alert_status_danger(response_json.text || 'Error creating outgoing job');
        } else {
          this.alertService.alert_status_success('Outgoing Job has been created - a proof will be created now if selected');
          this.matterStoreService.billingRecordTimeComponent!.addBilling(this.doc!);
          this.documentStoreService.documentSidebarComponent!.newDocument(true);
          this.matterStoreService.matterShowComponent!.outgoingJobsIndexComponent.search();
          this.result.emit('success');
        }
      })
      .catch((err) => {
        console.error(err);
        this.creatingOutgoingJob = false;
        this.alertService.alert_status_clear_overlay();
        this.alertService.alert_status_danger('Error creating outgoing job');
      });
  }

  formatMailTo(documentContact: DocumentContact, attachment: any = null): string {
    if (attachment) {
      var term = 'copy';
      if (attachment.quality)
        if (Number(attachment.copies) > 1) term = 'qualities';
        else term = 'qualities';
      else if (Number(attachment.copies) > 1) term = 'copies';
      return Number(attachment.copies) + ' ' + term + ' will be sent by ' + documentContact.mail_type + ' mail';
    } else {
      var copies = Number(this.outgoingTransaction.copies);
      var term = 'original';
      if (copies > 1) term = 'originals';
      return copies + ' ' + term + ' will be sent by ' + documentContact.mail_type + ' mail';
    }
  }

  formatEmailTo(documentContact: DocumentContact, attachment: any = null) {
    if (attachment) {
      var term = 'copy';
      if (Number(attachment.copies) > 1) term = 'copies';
      return Number(attachment.copies) + ' ' + term + ' will be sent by email to ' + documentContact.copy_of_email_address;
    } else {
      var copies = Number(this.outgoingTransaction.copies);
      var term = 'original';
      if (copies > 1) term = 'originals';
      return copies + ' ' + term + ' will be sent by email to ' + documentContact.copy_of_email_address;
    }
  }

  formatFaxTo(documentContact: DocumentContact, attachment: any = null) {
    if (attachment) {
      var term = 'copy';
      if (Number(attachment.copies) > 1) term = 'copies';
      return attachment.copies + ' ' + term + ' will be sent by fax to ' + documentContact.copy_of_fax;
    } else {
      var copies = Number(this.outgoingTransaction.copies);
      var term = 'original';
      if (copies > 1) term = 'originals';
      return copies + ' ' + term + ' will be sent by fax to ' + documentContact.copy_of_fax;
    }
  }

  formatMail(documentContact: DocumentContact, attachment: any = null) {
    if (attachment) {
      var term = 'copy';
      if (attachment.quality) term = 'copy';
      return '1 ' + term + ' will be sent by ' + documentContact.mail_type + ' mail';
    } else {
      return '1 copy will be sent by ' + documentContact.mail_type + ' mail';
    }
  }

  formatEmail(documentContact: DocumentContact, attachment: any = null) {
    if (attachment) {
      return '1 copy will be sent by email to ' + documentContact.copy_of_email_address;
    } else {
      return '1 copy will be sent by email to ' + documentContact.copy_of_email_address;
    }
  }

  formatFax(documentContact: DocumentContact, attachment: any = null) {
    if (attachment) {
      return '1 copy will be sent by fax to ' + documentContact.copy_of_fax;
    } else {
      return '1 copy will be sent by fax to ' + documentContact.copy_of_fax;
    }
  }

  formatProofDate(date: string): string | void {
    if (!date) return;
    let date_array: Array<string> = date.split('/');
    let month = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'][Number(date_array[0]) - 1];
    let year = date_array[2];
    let day = date_array[1];
    if ((Number(day) == 1 || Number(day) % 10 == 1) && Number(day) != 11) day += 'st';
    else if ((Number(day) == 2 || Number(day) % 10 == 2) && Number(day) != 12) day += 'nd';
    else if ((Number(day) == 3 || Number(day) % 10 == 3) && Number(day) != 13) day += 'rd';
    else day += 'th';
    return day + ' day of ' + month + ', ' + year;
  }

  formatRecipientForProof(documentContact: DocumentContact) {
    var html = 'By';
    var types = [];
    var type_result = '';
    var name_section = documentContact.copy_of_contact + '<br />';
    if (documentContact.send_by_mail) {
      types.push(documentContact.mail_type + ' mail');
      name_section += (documentContact.copy_of_address || '').replace(/\n/g, '<br>');
    }
    if (documentContact.send_by_email) {
      types.push('email');
      name_section += documentContact.copy_of_email_address + '<br />';
    }
    if (documentContact.send_by_fax) {
      types.push('fax');
      name_section += documentContact.copy_of_fax + '<br />';
    }
    var types_len = types.length;
    for (var i in types) {
      var punctuation = '';
      if (Number(i) == 0) punctuation += ' ';
      else if (Number(i) > 0 && Number(i) == types_len - 1) punctuation += ' and ';
      else punctuation += ', ';
      html += punctuation + types[i];
    }
    html += ':<br />';
    html += name_section;
    return html;
  }

  onPayeeSelect(contact: any) {
    this.outgoingTransaction.check_payee_id = Number(contact.id);
    this.outgoingTransaction.check_payee_name = contact.label;
  }

  setOutgoingJobSummaryAllowed(): boolean {
    if (this.documentToContacts.length < 1) return false;
    if (this.doc!.attachments_included && this.attachments.length < 1) return false;
    if (this.outgoingTransaction.include_proof && (!this.outgoingTransaction.proof_date || !this.outgoingTransaction.type_served)) return false;
    if (this.outgoingTransaction.needs_check && (!this.outgoingTransaction.check_payee_id || !this.outgoingTransaction.check_amount)) return false;
    if (this.outgoingTransaction.wait_for_attachments && !this.outgoingTransaction.describe) return false;
    for (let i in this.documentToContacts) {
      let dc = this.documentToContacts[i];
      if (!dc.send_by_mail && !dc.send_by_email && !dc.send_by_fax) return false;
    }
    for (var i in this.documentCcContacts) {
      var dc = this.documentCcContacts[i];
      if (!dc.send_by_mail && !dc.send_by_email && !dc.send_by_fax) return false;
    }
    for (var i in this.documentBccContacts) {
      var dc = this.documentBccContacts[i];
      if (!dc.send_by_mail && !dc.send_by_email && !dc.send_by_fax) return false;
    }
    return true;
  }

  previewProof(iframeId: string) {
    let errors: Array<string> = [];
    if (!this.outgoingTransaction.proof_date) errors.push('Proof date is required');
    if (!this.outgoingTransaction.type_served) errors.push('Type served is required');
    if (errors.length > 0) {
      this.alertService.alert_status_danger(errors.join('<br />'));
      return;
    }
    var inputs = '';
    for (var i in this.documentToContacts) inputs += '<textarea name="recipients[]">' + (this.formatRecipientForProof(this.documentToContacts[i]) || '').replace(/'/g, "\\'") + '</textarea>';
    for (var i in this.documentCcContacts) inputs += '<textarea name="recipients[]">' + (this.formatRecipientForProof(this.documentCcContacts[i]) || '').replace(/'/g, "\\'") + '</textarea>';
    inputs += '<input type="hidden" name="matter_id" value="' + this.matter_id + '" />';
    inputs += '<input type="hidden" name="proof_date" value="' + this.formatProofDate(this.outgoingTransaction.proof_date) + '" />';
    inputs += '<input type="hidden" name="type_served" value="' + this.outgoingTransaction.type_served + '" />';
    inputs += '<input type="hidden" name="initial_run" value="' + true + '" />';
    inputs += '<input type="hidden" name="doc_template_id" value="' + (this.selectedProofTemplateId || '') + '" />';
    if (iframeId) {
      inputs += '<input type="hidden" name="disposition" value="inline" />';
      jQuery('<form target="' + iframeId + '" action="/documents/preview_proof.pdf" method="post">' + inputs + '</form>')
        .appendTo('body')
        .submit()
        .remove();
    } else {
      inputs += '<input type="hidden" name="disposition" value="attachment" />';
      jQuery('<form action="/documents/preview_proof.pdf" method="post">' + inputs + '</form>')
        .appendTo('body')
        .submit()
        .remove();
    }
  }

  navChanged(event: any) {
    console.log(event)
    if (event.nextId == 'summary') {
      this.outgoingJobSummaryAllowed = this.setOutgoingJobSummaryAllowed();
      this.buildMatterSummaryContent();
    }
  }
}
